/**
 * Bluetooth Print Service
 * Direct Bluetooth printing using Web Bluetooth API for Chrome/Edge on Android
 * This service connects directly to Bluetooth thermal printers (like POS-58)
 * without requiring any intermediate app like RAWBT
 */

import { Order, OrderItem, OrderDiscount } from '../types/orders';
import { formatInTz, DEFAULT_TIMEZONE } from '../utils/dateHelpers';

// Group identical items by product+variants+unitPrice, summing quantities and totals
const groupOrderItems = (items: OrderItem[]) => {
  const map = new Map<string, { item: OrderItem; qty: number; total: number }>();
  for (const item of items) {
    const variantsKey = (item.variants || []).map(v => v.variantName).sort().join(',');
    const key = `${item.productName}|${variantsKey}|${item.unitPrice}`;
    const existing = map.get(key);
    if (existing) {
      existing.qty += item.quantity;
      existing.total += item.total;
    } else {
      map.set(key, { item, qty: item.quantity, total: item.total });
    }
  }
  return Array.from(map.values());
};

/**
 * Holds the active restaurant timezone for ticket timestamps. See
 * printAgentService.ts for rationale.
 */
let activeTimeZone: string = DEFAULT_TIMEZONE;

export const setBluetoothPrintTimeZone = (tz: string): void => {
  activeTimeZone = tz || DEFAULT_TIMEZONE;
};

/**
 * Returns "now" formatted as a local ISO string (no Z suffix) in the
 * configured restaurant timezone.
 */
function getLocalDateTimeString(): string {
  return formatInTz(new Date(), activeTimeZone, 'YYYY-MM-DDTHH:mm:ss');
}

export interface PrintResult {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Bluetooth Print Service
 * Uses Web Bluetooth API to communicate directly with thermal printers
 */
export class BluetoothPrintService {
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private isConnected: boolean = false;

  // Serial Port Service UUIDs (trying multiple standards for compatibility)
  private readonly SERVICE_UUIDS = [
    '000018f0-0000-1000-8000-00805f9b34fb', // Serial Port Profile
    '0000ff00-0000-1000-8000-00805f9b34fb', // Common for Chinese printers
    '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip Transparent UART
  ];

  private readonly CHARACTERISTIC_UUIDS = [
    '00002af1-0000-1000-8000-00805f9b34fb', // SPP Write
    '0000ff02-0000-1000-8000-00805f9b34fb', // Common write characteristic
    '49535343-8841-43f4-a8d4-ecbe34729bb3', // Microchip TX
  ];

  /**
   * Check if Web Bluetooth API is available
   */
  isAvailable(): boolean {
    if (typeof navigator === 'undefined') return false;
    return 'bluetooth' in navigator;
  }

  /**
   * Connect to a Bluetooth printer
   * Shows browser's device picker dialog
   */
  async connect(): Promise<boolean> {
    try {
      if (!this.isAvailable()) {
        console.error('❌ Web Bluetooth API not available');
        return false;
      }

      console.log('🔵 Requesting Bluetooth device...');

      // Request device - accept all devices since we'll try multiple UUIDs
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: this.SERVICE_UUIDS,
      });

      if (!this.device) {
        console.error('❌ No device selected');
        return false;
      }

      console.log(`🔵 Selected device: ${this.device.name || 'Unknown'}`);

      // Connect to GATT server
      const server = await this.device.gatt?.connect();
      if (!server) {
        console.error('❌ Failed to connect to GATT server');
        return false;
      }

      console.log('✅ Connected to GATT server');

      // Try each service UUID until we find one that works
      let foundService = false;
      for (const serviceUuid of this.SERVICE_UUIDS) {
        try {
          console.log(`🔵 Trying service UUID: ${serviceUuid}`);
          const service = await server.getPrimaryService(serviceUuid);
          console.log(`✅ Got service for UUID: ${serviceUuid}`);

          // Try each characteristic UUID for this service
          for (const charUuid of this.CHARACTERISTIC_UUIDS) {
            try {
              console.log(`🔵 Trying characteristic UUID: ${charUuid}`);
              this.characteristic = await service.getCharacteristic(charUuid);
              console.log(`✅ Got characteristic for UUID: ${charUuid}`);
              foundService = true;
              break;
            } catch (charError) {
              console.log(`⚠️ Characteristic ${charUuid} not found, trying next...`);
            }
          }

          if (foundService) break;
        } catch (serviceError) {
          console.log(`⚠️ Service ${serviceUuid} not found, trying next...`);
        }
      }

      if (!foundService || !this.characteristic) {
        console.error('❌ Could not find compatible service/characteristic');
        return false;
      }

      this.isConnected = true;

      // Listen for disconnection
      this.device.addEventListener('gattserverdisconnected', this.onDisconnected.bind(this));

      console.log('✅ Successfully connected to printer');
      return true;
    } catch (error: any) {
      console.error('❌ Bluetooth connection error:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
      });
      this.isConnected = false;
      return false;
    }
  }

  /**
   * Handle device disconnection
   */
  private onDisconnected() {
    console.log('🔴 Bluetooth device disconnected');
    this.isConnected = false;
    this.characteristic = null;
  }

  /**
   * Disconnect from the printer
   */
  async disconnect(): Promise<void> {
    if (this.device && this.device.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.isConnected = false;
    this.characteristic = null;
    this.device = null;
  }

  /**
   * Check if currently connected to a printer
   */
  isDeviceConnected(): boolean {
    return this.isConnected && this.characteristic !== null;
  }

  /**
   * Get connected device name
   */
  getDeviceName(): string | null {
    return this.device?.name || null;
  }

  /**
   * Send raw bytes to the printer
   * Splits large data into chunks to avoid MTU limitations
   */
  private async sendBytes(data: Uint8Array): Promise<boolean> {
    if (!this.characteristic) {
      console.error('❌ Not connected to printer - no characteristic');
      return false;
    }

    try {
      console.log(`🔵 Starting to send ${data.length} bytes to printer...`);

      // Split data into chunks - BLE MTU is typically 20 bytes for older devices
      // Using small chunks with delays for reliability
      const CHUNK_SIZE = 100;
      const CHUNK_DELAY = 50; // ms between chunks
      let offset = 0;
      let chunkCount = 0;

      while (offset < data.length) {
        const chunk = data.slice(offset, offset + CHUNK_SIZE);
        await this.characteristic.writeValue(chunk);
        chunkCount++;
        offset += CHUNK_SIZE;

        // Delay between chunks to avoid overwhelming the printer buffer
        if (offset < data.length) {
          await new Promise(resolve => setTimeout(resolve, CHUNK_DELAY));
        }
      }

      console.log(`✅ Successfully sent ${data.length} bytes in ${chunkCount} chunks to printer`);
      return true;
    } catch (error: any) {
      console.error('❌ Error sending bytes:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      return false;
    }
  }

  /**
   * Convert base64 string to Uint8Array
   */
  private base64ToBytes(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Generate ESC/POS bytes using backend API
   * This ensures consistency across all platforms (Windows, Android)
   */
  private async generateEscPosBytes(requestData: any): Promise<Uint8Array | null> {
    try {
      // Use the backend API to generate ESC/POS bytes
      // This works from anywhere (no need for local Print Agent)
      // Use REACT_APP env var (Create React App), remove /api suffix to get base URL
      const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || 'https://api.massresto.com';
      const endpoint = `${baseUrl}/api/escpos/generate`;

      console.log(`🔵 Generating ESC/POS bytes via API: ${endpoint}`);
      console.log(`🔵 Request data:`, requestData);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API error: ${response.status} - ${errorText}`);
        return null;
      }

      const result = await response.json();
      if (result.success && result.data) {
        console.log(`✅ Generated ${result.byteCount} bytes from API`);
        // Convert base64 to bytes
        const bytes = this.base64ToBytes(result.data);
        console.log(`✅ Converted to ${bytes.length} bytes (should match ${result.byteCount})`);

        return bytes;
      } else {
        console.error('API returned error:', result.error);
        return null;
      }
    } catch (error: any) {
      console.error('Error generating ESC/POS bytes from API:', error);
      return null;
    }
  }

  /**
   * Ensure connection before printing
   */
  private async ensureConnection(): Promise<boolean> {
    if (this.isDeviceConnected()) {
      return true;
    }

    console.log('🔵 Not connected, initiating connection...');
    return await this.connect();
  }

  /**
   * Print a pre-check/pre-bill receipt
   */
  async printPreCheck(
    order: Order,
    restaurantName: string,
    restaurantAddress: string | undefined,
    restaurantPhone: string | undefined,
    tableName: string,
    waiterName: string,
    subtotal: number,
    tax: number,
    taxRate: number,
    discounts: OrderDiscount[],
    total: number,
    autofacturaUrl?: string
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing pre-check via Bluetooth');

    // Match C# DTO format exactly: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 0, // PreCheck = 0 in C# enum
      Receipt: {
        RestaurantName: restaurantName,
        Address: restaurantAddress || null,
        Phone: restaurantPhone || null,
        Title: 'PRE-CUENTA',
        TableName: tableName,
        WaiterName: waiterName,
        DateTime: getLocalDateTimeString(),
        Items: groupOrderItems(order.orderItems.filter(item => !item.isCancelled))
          .map(({ item, qty, total: itemTotal }) => ({
            Quantity: qty,
            Name: item.productName,
            UnitPrice: item.unitPrice,
            Total: itemTotal,
            Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
            Notes: null,
          })),
        Subtotal: subtotal,
        Tax: tax,
        TaxRate: taxRate,
        Discounts: discounts.map(d => ({
          Reason: d.reason || 'Descuento',
          Amount: d.discountAmount,
        })),
        Tip: 0,
        Total: total,
        Payments: [],
        Footer: '*** PRE-CUENTA ***\nNo es un comprobante fiscal',
        AutofacturaUrl: autofacturaUrl || null,
        OrderNumber: order.orderNumber || null,
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Pre-cuenta impresa correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print a payment receipt
   */
  async printPayment(
    order: Order,
    restaurantName: string,
    restaurantAddress: string | undefined,
    restaurantPhone: string | undefined,
    tableName: string,
    waiterName: string,
    subtotal: number,
    tax: number,
    taxRate: number,
    discounts: OrderDiscount[],
    tip: number,
    total: number,
    payments: any[],
    autofacturaUrl?: string
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing payment receipt via Bluetooth');

    // Get filtered items
    const filteredItems = order.orderItems.filter(item => !item.isCancelled);

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 1, // Payment = 1 in C# enum
      Receipt: {
        RestaurantName: restaurantName,
        Address: restaurantAddress || null,
        Phone: restaurantPhone || null,
        Title: 'TICKET DE PAGO',
        TableName: tableName,
        WaiterName: waiterName,
        DateTime: getLocalDateTimeString(),
        Items: groupOrderItems(filteredItems).map(({ item, qty, total: itemTotal }) => ({
            Quantity: qty,
            Name: item.productName,
            UnitPrice: item.unitPrice,
            Total: itemTotal,
            Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
            Notes: null,
          })),
        Subtotal: subtotal,
        Tax: tax,
        TaxRate: taxRate,
        Discounts: discounts.map(d => ({
          Reason: d.reason || 'Descuento',
          Amount: d.discountAmount,
        })),
        Tip: tip,
        Total: total,
        Payments: payments.map(p => ({
          Method: p.methodId,
          Amount: p.amount,
          Reference: p.reference || null,
        })),
        Footer: 'Gracias por su visita',
        // Delivery/TakeOut info
        IsDelivery: order.type === 'Delivery',
        IsTakeOut: order.type === 'TakeOut',
        CustomerName: order.customerName || null,
        CustomerPhone: order.customerPhone || null,
        DeliveryAddress: order.deliveryAddress || null,
        DeliveryNotes: order.deliveryNotes || null,
        AutofacturaUrl: autofacturaUrl || null,
        OrderNumber: order.orderNumber || null,
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Ticket impreso correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print a kitchen order (comanda)
   */
  async printKitchenOrder(
    order: Order,
    tableName: string,
    waiterName: string,
    newItems: OrderItem[]
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing kitchen order via Bluetooth');

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 2, // KitchenOrder = 2 in C# enum
      Receipt: {
        RestaurantName: 'COCINA',
        Address: null,
        Phone: null,
        // Header for the Bluetooth path. It prints all items on one ticket (no
        // per-production-center split), so it stays generic "COCINA". The backend
        // generator now renders Title as the kitchen header.
        Title: 'COCINA',
        TableName: tableName,
        WaiterName: waiterName,
        OrderNumber: order.orderNumber || null,
        DateTime: getLocalDateTimeString(),
        Items: newItems.map((item: OrderItem) => ({
          Quantity: item.quantity,
          Name: item.productName,
          UnitPrice: item.unitPrice,
          Total: item.total,
          Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
          Notes: item.notes || null,
        })),
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: 0,
        Payments: [],
        Footer: '*** COMANDA ***',
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Comanda impresa correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print a test receipt
   */
  async printTest(): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing test page via Bluetooth');

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 3, // Test = 3 in C# enum
      Receipt: {
        RestaurantName: 'MASSRESTAURANT',
        Address: null,
        Phone: null,
        Title: 'TEST DE IMPRESION',
        TableName: 'Test',
        WaiterName: null,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: 0,
        Payments: [],
        Footer: 'Impresora Bluetooth conectada correctamente',
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Prueba impresa correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print cash count receipt (Arqueo de Caja)
   */
  async printCashCount(
    restaurantName: string,
    userName: string,
    expectedAmount: number,
    actualAmount: number,
    variance: number,
    denominationsText: string
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing cash count via Bluetooth');

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 4, // CashCount = 4 in C# enum
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'ARQUEO DE CAJA',
        TableName: '',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: actualAmount,
        Payments: [],
        Footer: `Esperado: $${expectedAmount.toFixed(2)}\nContado: $${actualAmount.toFixed(2)}\nDiferencia: ${variance >= 0 ? '+' : ''}$${variance.toFixed(2)}\n\n${denominationsText}`,
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Arqueo impreso correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print cash movement receipt (Ingreso/Retiro de Caja)
   */
  async printCashMovement(
    restaurantName: string,
    userName: string,
    movementType: 'CORRECTION' | 'DROP',
    amount: number,
    reason: string,
    reference?: string
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing cash movement via Bluetooth');

    const typeLabel = movementType === 'CORRECTION' ? 'INGRESO' : 'RETIRO';

    // Build footer with movement details
    let footer = `Tipo: ${typeLabel}\n`;
    footer += `Motivo: ${reason}\n`;
    if (reference) {
      footer += `Referencia: ${reference}\n`;
    }
    footer += '\nComprobante de Movimiento';

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 6, // CashMovement = 6 in C# enum
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'MOVIMIENTO DE CAJA',
        TableName: '',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: amount,
        Payments: [],
        Footer: footer,
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Movimiento de caja impreso correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }

  /**
   * Print cash register closure receipt (Cierre de Caja)
   */
  async printCashRegisterClosure(
    restaurantName: string,
    userName: string,
    openTime: string,
    initialAmount: number,
    totalSales: number,
    totalTips: number,
    expectedAmount: number,
    finalAmount: number,
    variance: number,
    totalCreditCardSales: number,
    totalDebitCardSales: number,
    totalTransferSales: number,
    totalOtherSales: number,
    totalAdults: number,
    totalChildren: number,
    notes?: string
  ): Promise<PrintResult> {
    if (!await this.ensureConnection()) {
      return {
        success: false,
        message: 'No se pudo conectar con la impresora Bluetooth',
        error: 'Connection failed',
      };
    }

    console.log('🖨️ Printing cash register closure via Bluetooth');

    // Build footer with closure details
    let footer = `APERTURA: ${openTime}\n`;
    footer += `Monto Inicial: $${initialAmount.toFixed(2)}\n`;
    footer += `Total Ventas: $${totalSales.toFixed(2)}\n`;
    footer += `Total Propinas: $${totalTips.toFixed(2)}\n`;
    footer += `\n--- Ventas por Método ---\n`;
    footer += `Efectivo: $${totalSales.toFixed(2)}\n`;
    footer += `T. Crédito: $${totalCreditCardSales.toFixed(2)}\n`;
    footer += `T. Débito: $${totalDebitCardSales.toFixed(2)}\n`;
    footer += `Transferencia: $${totalTransferSales.toFixed(2)}\n`;
    footer += `Otros: $${totalOtherSales.toFixed(2)}\n`;
    footer += `\n--- Comensales ---\n`;
    footer += `Adultos: ${totalAdults}\n`;
    footer += `Niños: ${totalChildren}\n`;
    footer += `Total: ${totalAdults + totalChildren}\n`;
    footer += `\nEsperado: $${expectedAmount.toFixed(2)}\n`;
    footer += `Contado: $${finalAmount.toFixed(2)}\n`;
    footer += `Diferencia: ${variance >= 0 ? '+' : ''}$${variance.toFixed(2)}\n`;
    if (notes) {
      footer += `\nNotas: ${notes}\n`;
    }
    footer += '\n*** CIERRE DE CAJA ***';

    // Match C# DTO format: Type (number enum) and Receipt (PascalCase properties)
    const requestData = {
      Type: 5, // CashRegisterClosure = 5 in C# enum
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'CIERRE DE CAJA',
        TableName: '',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: finalAmount,
        Payments: [],
        Footer: footer,
      },
    };

    const bytes = await this.generateEscPosBytes(requestData);
    if (!bytes) {
      return {
        success: false,
        message: 'No se pudieron generar los datos de impresión',
        error: 'Generation failed',
      };
    }

    const sent = await this.sendBytes(bytes);
    return {
      success: sent,
      message: sent ? 'Cierre de caja impreso correctamente' : 'Error al enviar datos a la impresora',
      error: sent ? undefined : 'Send failed',
    };
  }
}

// Export singleton instance
export const bluetoothPrintService = new BluetoothPrintService();
